import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Grid, Navigation } from 'swiper/modules';
import 'swiper/css/pagination';
import instance from "../../services/services";
import { BANNER_LIST_URL, CREDIT_LIST_URL, CATEGORY_LIST_URL, DATA_LIST_URL } from "../../utils/urls";
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import ProductSection from "../../components/Product/ProductSection";
import CommonBanner from "../../components/Banner/CommonBanner";
import useUserStore from "../../store/userStore";
import SupportPopup from "../../components/Popup/SupportPopup";
import { useLanguage } from "../../hooks/useLanguage";

function CinemaMain() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const userInfo = useUserStore((state) => state.userInfo);
    const [bannerList, setBannerList] = useState([]); // 배너 리스트
    const [creditList, setCreditList] = useState([]); // 크레딧 리스트
    const [isTablet, setIsTablet] = useState(false);
    const [categoryList, setCategoryList] = useState([]); // 카테고리 리스트
    const [activeTab, setActiveTab] = useState('all'); // 활성화된 탭을 관리하는 state 추가
    const [productSections, setProductSections] = useState({
        new: [],
        camera: [],
        lens: [],
        etc: [],
        acc: []
    });
    const [subBanners, setSubBanners] = useState({
        middle1: [],
        middle2: [],
        middle3: [],
        middle4: []
    });
    const [sectionCounts, setSectionCounts] = useState({
        new: 0,
        camera: 0,
        lens: 0,
        etc: 0,
        acc: 0
    });

    const [selectedWorkSeq, setSelectedWorkSeq] = useState(null);
    const [supportPop, setSupportPop] = useState(false);


    // 반응형 처리
    useEffect(() => {
        const handleResize = () => {
            setIsTablet(window.innerWidth < 1024);
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 배너 데이터 가져오기
    useEffect(() => {
        const fetchBannerList = async () => {
            try {
                const response = await instance.post(BANNER_LIST_URL, {
                    b_type: "main",
                    main_kind: "CINEMA"
                });
                setBannerList(response.data.data);
            } catch (error) {
                console.error("배너 목록 가져오기 실패", error);
            }
        };
        fetchBannerList();
    }, []);

    // 크레딧 데이터 가져오기
    useEffect(() => {
        const fetchCreditList = async () => {
            try {
                const response = await instance.post(CREDIT_LIST_URL); // API 엔드포인트는 실제 URL로 변경해주세요
                let creditData = response.data.data.data;
                if (isTablet) {
                    creditData = creditData.slice(2); // 태블릿에서는 처음 2개 항목 제외
                }
                setCreditList(creditData);
            } catch (error) {
                console.error("크레딧 목록 가져오기 실패", error);
            }
        };
        fetchCreditList();
    }, [isTablet]);

    // 카테고리 목록
    useEffect(()=> {
        const fetchCategoryList = async () => {
            try {
                const response = await instance.post(CATEGORY_LIST_URL);
                // 전체 카테고리 데이터를 그대로 저장 (다른 곳에서도 사용하므로)
                setCategoryList(response.data.data);
            } catch (error) {
                console.error("카테고리 목록 가져오기 실패", error);
            }
        };
        fetchCategoryList();
    }, []);

    // NEW ARRIVAL 탭용 카테고리 목록 (Purpose 제외)
    const getFilteredCategoryList = () => {
        return categoryList.filter(category => 
            category.category_name !== 'Purpose' && 
            category.category_name !== 'PURPOSE' &&
            category.category_name !== 'BRAND'
        );
    };

    // 섹션 설정을 useMemo로 감싸기
    const SECTIONS_CONFIG = React.useMemo(() => [
        {
            id: 'new',
            title: 'NEW ARRIVAL',
            goods_type: 'new',
            showTabs: true,
            nextBanner: 'middle1'
        },
        {
            id: 'camera',
            title: 'CAMERA',
            goods_type: 'camera',
            showTabs: false,
            nextBanner: 'middle2'
        },
        {
            id: 'lens',
            title: 'LENS',
            goods_type: 'lens',
            showTabs: false,
            nextBanner: 'middle3'
        },
        {
            id: 'etc',
            title: 'ETC',
            goods_type: 'etc',
            showTabs: false,
            nextBanner: 'middle4'
        },
        {
            id: 'acc',
            title: 'ACCESSORY',
            goods_type: 'acc',
            showTabs: false
        }
    ], []);

    // 모든 섹션의 데이터 가져오기
    useEffect(() => {
        const fetchAllSectionData = async () => {
            try {
                // 첫 번째 페이지 데이터 가져오기 (offset: 1)
                const firstPageResponses = await Promise.all(
                    SECTIONS_CONFIG.map(section => 
                        instance.post(DATA_LIST_URL, {
                            category_code: "",
                            goods_type: section.goods_type,
                            main_kind: "cinema",
                            member_seq: userInfo.member_seq ? userInfo.member_seq : "",
                            offset: 1,
                            limit: section.goods_type === 'new' ? 20 : 10
                        })
                    )
                );

                // 추가 페이지가 필요한 섹션 확인 및 데이터 가져오기
                const additionalDataPromises = [];
                const sectionsNeedingMoreData = [];

                // 필요한 경우에만 추가 데이터 가져오기
                const additionalResponses = additionalDataPromises.length > 0 
                    ? await Promise.all(additionalDataPromises) 
                    : [];

                // 섹션 카운트 저장
                const newSectionCounts = {};
                firstPageResponses.forEach((response, index) => {
                    const sectionId = SECTIONS_CONFIG[index].id;
                    newSectionCounts[sectionId] = response.data.data.cntRow;
                });
                setSectionCounts(newSectionCounts);

                // 데이터 병합
                setProductSections(prevSections => {
                    const newProductSections = { ...prevSections };
                    firstPageResponses.forEach((response, index) => {
                        const sectionId = SECTIONS_CONFIG[index].id;
                        const firstPageData = response.data.data.data;
                        
                        if (sectionsNeedingMoreData.includes(sectionId)) {
                            // 추가 데이터가 있는 경우 병합
                            const additionalIndex = sectionsNeedingMoreData.indexOf(sectionId);
                            const additionalData = additionalResponses[additionalIndex].data.data.data;
                            newProductSections[sectionId] = [...firstPageData, ...additionalData];
                        } else {
                            // 첫 페이지 데이터만 사용
                            newProductSections[sectionId] = firstPageData;
                        }
                    });
                    return newProductSections;
                });

            } catch (error) {
                console.error("상품 데이터 가져오기 실패", error);
            }
        };

        fetchAllSectionData();
    }, [ SECTIONS_CONFIG, userInfo.member_seq ]);

    // 모든 서브 배너 데이터 가져오기
    useEffect(() => {
        const fetchAllSubBanners = async () => {
            try {
                const bannerTypes = ['middle1', 'middle2', 'middle3', 'middle4'];
                const responses = await Promise.all(
                    bannerTypes.map(type => 
                        instance.post(BANNER_LIST_URL, {
                            b_type: type,
                            main_kind: "CINEMA"
                        })
                    )
                );

                const newSubBanners = {};
                responses.forEach((response, index) => {
                    newSubBanners[bannerTypes[index]] = response.data.data;
                });
                setSubBanners(newSubBanners);
            } catch (error) {
                console.error("서브 배너 목록 가져오기 실패", error);
            }
        };

        fetchAllSubBanners();
    }, []);


    // 탭 클릭 핸들러 수정
    const handleTabClick = async (tabId) => {
        setActiveTab(tabId);
        
        try {
            // 선택된 카테고리 찾기
            const selectedCategory = categoryList.find(category => category.category_seq === tabId);
            
            const response = await instance.post(DATA_LIST_URL, {
                category_code: tabId === 'all' ? "" : selectedCategory?.category_code,
                goods_type: "new",
                main_kind: "cinema",
                member_seq: userInfo.member_seq ? userInfo.member_seq : "",
                offset: 1,
                limit: 30
            });

            // NEW ARRIVAL 섹션의 데이터만 업데이트 (다른 섹션은 그대로 유지)
            setProductSections(prevSections => {
                const newSections = { ...prevSections };
                // new 섹션의 데이터만 응답 데이터로 교체 (빈 배열일 경우도 처리)
                newSections.new = response.data.data.data || [];
                return newSections;
            });

            // NEW ARRIVAL 섹션의 카운트도 업데이트
            setSectionCounts(prevCounts => {
                const newCounts = { ...prevCounts };
                newCounts.new = parseInt(response.data.data.cntRow) || 0;
                return newCounts;
            });
        } catch (error) {
            console.error("카테고리 데이터 가져오기 실패", error);
            // 에러 시 빈 데이터로 설정하여 UI 깨짐 방지
            setProductSections(prevSections => {
                const newSections = { ...prevSections };
                newSections.new = [];
                return newSections;
            });
            setSectionCounts(prevCounts => {
                const newCounts = { ...prevCounts };
                newCounts.new = 0;
                return newCounts;
            });
        }
    };

    // 더보기 클릭 핸들러 추가
    const handleMoreClick = (sectionId) => {
        const typeMap = {
            'new': 'new',
            'camera': 'camera',
            'lens': 'lens',
            'etc': 'etc',
            'acc': 'acc'
        };
        
        const sectionType = typeMap[sectionId];
        if (sectionType) {
            // 현재 URL에서 type 값을 가져옴
            const currentType = searchParams.get('type'); 
            navigate(`/mainMore/${sectionType}/${currentType}`);
        }
    };
    
    const handleViewMore = (workSeq) => {
        setSelectedWorkSeq(workSeq);
        setSupportPop(true);
    };

    return (
        <div>
            {/* 배너 섹션 */}
            <div id="main-banner">
                {bannerList.length > 0 && (
                    <Swiper
                        modules={[Pagination, Autoplay]}
                        pagination={{ clickable: true }}
                        autoplay={{ delay: 5000 }}
                        slidesPerView="auto"
                        loop={true}
                        className="main_visual_swiper banner"
                    >
                        {bannerList.map((banner, idx) => (
                            <SwiperSlide 
                                key={idx}
                            >
                                <img
                                    src={isTablet && banner.mobile_img ? banner.mobile_img : banner.pc_img}
                                    alt={`메인배너 ${idx + 1}`}
                                    className="object-cover"
                                    loading="lazy"
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </div>

            {/* 크레딧 섹션 */}
            <div id="credits-section" className="main_prod_list credit">
                <div className="inner">
                    <div className="credit_title_wrap">
                        <div className="credit_title">CREDITS</div>
                        <button className="more_text_btn flex-center-2" onClick={() => navigate(`/community/credit`)}>{t('see_more')}</button>
                    </div>
                    <ul className="credit_list resp_container">
                        {creditList.map((credit, index) => (
                            <li
                                key={index}
                                className="credit_item"
                                onClick={() => handleViewMore(credit.credit_seq)}  
                            >
                                <img
                                    src={credit.files[0].img_url}
                                    alt={`${credit.work_title} 크레딧 이미지`}
                                    loading="lazy"
                                />
                                <span className="name ellipsis_clamp">
                                    <div dangerouslySetInnerHTML={{__html: credit.work_title}}></div>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* 상품 섹션들과 서브 배너들 */}
            {SECTIONS_CONFIG.map(section => {

                // 탭이 있는 섹션(new)은 데이터가 없어도 렌더링, 탭이 없는 섹션은 데이터가 없으면 렌더링하지 않음
                const sectionData = section.id === 'new' ? productSections.new : productSections[section.id];
                if (!section.showTabs && (!sectionData || sectionData.length === 0)) {
                    return null; // 탭이 없고 데이터가 없으면 아무것도 렌더링하지 않음
                }

                                
                // 섹션별 Swiper 옵션 설정
                const sectionSwiperOptions = {
                    slidesPerView: 2,
                    spaceBetween: 8,
                    autoHeight: false,
                    modules: [Pagination, Grid, Navigation],
                    pagination: { clickable: true },
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                    grid: {
                        rows: section.id === 'new' ? 2 : 1,
                        fill: 'row'
                    },
                    breakpoints: {
                        1280: {
                            slidesPerView: 5,
                            spaceBetween: 18,
                        },
                        768: {
                            slidesPerView: 4,
                            spaceBetween: 12,
                        }
                    }
                };

                return (
                    <div key={section.id} id={getSectionId(section.id)}>
                        <ProductSection
                            title={section.title}
                            products={section.id === 'new' ? productSections.new : productSections[section.id]}
                            swiperOptions={sectionSwiperOptions}
                            showTabs={section.showTabs}
                            categoryList={section.showTabs ? getFilteredCategoryList() : categoryList}
                            activeTab={activeTab}
                            onTabClick={handleTabClick}
                            sectionId={section.id}
                            onMoreClick={handleMoreClick}
                            showMoreButton={parseInt(sectionCounts[section.id]) > 0}
                        />
                        {section.nextBanner && subBanners[section.nextBanner]?.length > 0 && (
                            <CommonBanner
                                banners={subBanners[section.nextBanner]}
                                isTablet={isTablet}
                                className="sub_visual_swiper banner"
                            />
                        )}
                    </div>
                );
            })}
            
            {/* 팝업 컴포넌트 추가 */}
            <SupportPopup
                isPopup={supportPop}
                setIsPopup={setSupportPop}
                commType="credit"
                workSeq={selectedWorkSeq}
            />
        </div>
    );
}

// 섹션 ID 헬퍼 함수
function getSectionId(sectionId) {
    const idMap = {
        'new': 'new-arrival',
        'camera': 'cinema-camera',
        'lens': 'cinema-lens',
        'etc': 'etc-section',
        'acc': 'acc-section'
    };
    return idMap[sectionId] || sectionId;
}

export default CinemaMain;